import React, { useState, useEffect } from "react";
// eslint-disable-next-line import/no-extraneous-dependencies
import { useDispatch } from "react-redux";
import { Document, Page } from "react-pdf";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  Typography,
  Button,
  Modal,
  ModalHeader,
  ModalDialog,
  Stack,
  IconButton,
  Divider,
  styled,
} from "@financeable-com-au/financeable-ui";
import { DOCUMENT_TEMPLATE_TYPE } from "@/constants";
import { openSnackbar } from "@/store/slices/snackbarSlice";

interface PreviewDocumentProps {
  open: boolean;
  previewBuffer: string | ArrayBuffer | Uint8Array | null;
  confirmationLoader: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  templateType: string;
}

/**
 * Styles.
 */
const PDFContent = styled(Stack)`
  .react-pdf__Page {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  .react-pdf__Page__textContent {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    opacity: 0.2;
  }
  .react-pdf__Page__textContent span {
    color: transparent !important;
  }
  .react-pdf__Page__annotations {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
  }
`;

const PreviewDocument: React.FC<PreviewDocumentProps> = ({
  open,
  previewBuffer,
  confirmationLoader,
  onClose,
  onConfirm,
  title = "Preview Document",
  templateType,
}) => {
  const dispatch = useDispatch();
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [error, setError] = useState<string | null>(null);
  const [loadingDoc, setLoadingDoc] = useState<boolean>(false);

  // Reset states when previewBuffer changes
  useEffect(() => {
    if (previewBuffer) {
      setError(null);
      setPageNumber(1);
      setNumPages(0);
    }
  }, [previewBuffer]);

  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setError(null);
  };

  const handleDocumentLoadError = (error: Error) => {
    const errorMessage = error?.message || "Failed to load PDF document";
    setError(`Failed to load PDF document: ${errorMessage}`);
  };

  const handleZoomIn = () => {
    setScale((prevScale) => Math.min(prevScale + 0.25, 3.0));
  };

  const handleZoomOut = () => {
    setScale((prevScale) => Math.max(prevScale - 0.25, 0.5));
  };

  const goToPreviousPage = () => {
    setPageNumber((prevPage) => Math.max(prevPage - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber((prevPage) => Math.min(prevPage + 1, numPages));
  };

  // Convert buffer to ArrayBuffer format - react-pdf can accept ArrayBuffer directly
  const [pdfFile, setPdfFile] = useState<ArrayBuffer | Uint8Array | string | null>(null);

  useEffect(() => {
    if (!previewBuffer) {
      setPdfFile(null);
      return;
    }

    let objectUrl: string | null = null;

    const fetchPdf = async (s3Url: string) => {
      try {
        setLoadingDoc(true)
        const response = await fetch(s3Url);
        if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.status}`);
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        setPdfFile(objectUrl);
        setLoadingDoc(false)
      } catch (error) {
        setLoadingDoc(false)
        dispatch(openSnackbar({ message: "Failed to fetch PDF", intent: "danger" }));
      }
    };

    try {

      if (typeof previewBuffer === "string") {
        fetchPdf(previewBuffer);
      } else {
        setPdfFile(null);
        return;
      }
    } catch (e) {
      setPdfFile(null);
    }

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [previewBuffer]);

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="pdf-preview-modal" aria-describedby="pdf-preview-content">
      <ModalDialog
        padding={0}
        minWidth="90vw"
        maxWidth="1200px"
        style={{ height: "90vh", maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        {/* Header with title and action buttons */}
        <ModalHeader padding={4} hasModalClose={true}>
          <Stack flexDirection="row" alignItems="center" justifyContent="space-between" width="100%">
            <Typography level="title-md" bold>
              {title}
            </Typography>
            <Button variant="primary" onClick={onConfirm} disabled={confirmationLoader}>
              {confirmationLoader
                ? "Sending..."
                : `Send ${templateType === DOCUMENT_TEMPLATE_TYPE.CUSTOMER_DISCLOSURE ? "disclosure" : "privacy consent"}`}
            </Button>
          </Stack>
        </ModalHeader>

        {/* Controls for zoom and navigation */}
        <Stack flexDirection="row" alignItems="center" justifyContent="space-between" px={2} py={2} gap={2}>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ bgcolor: "white", borderRadius: 1, px: 0.5, py: 0.25 }}
          >
            <IconButton onClick={handleZoomOut} disabled={scale <= 0.5} variant="inline" intent="neutral">
              <ZoomOutIcon fontSize="small" />
            </IconButton>
            <Typography level="body-sm" bold>
              {Math.round(scale * 100)}%
            </Typography>
            <IconButton onClick={handleZoomIn} disabled={scale >= 3.0} variant="inline" intent="neutral">
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Stack>

          {numPages > 0 && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button variant="secondary" onClick={goToPreviousPage} disabled={pageNumber <= 1}>
                <ArrowBackIosNewIcon fontSize="small" />
              </Button>
              <Typography level="body-sm" bold>
                Page {pageNumber} of {numPages}
              </Typography>
              <Button variant="secondary" onClick={goToNextPage} disabled={pageNumber >= numPages}>
                <ArrowForwardIosIcon fontSize="small" />
              </Button>
            </Stack>
          )}
        </Stack>
        <Divider />

        {/* PDF Content */}
        <PDFContent
          flex={1}
          overflow="auto"
          justifyContent="flex-start"
          alignItems="center"
          p={1.5}
          minHeight={0}
          textAlign="center"
        >
          {error && <Typography textColor="red">{error}</Typography>}

          {!error && pdfFile && (
            <Document
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              file={pdfFile as any}
              onLoadSuccess={handleDocumentLoadSuccess}
              onLoadError={handleDocumentLoadError}
              loading={
                <Stack alignItems="center" justifyContent="center" p={4}>
                  <Typography>Loading document...</Typography>
                </Stack>
              }
            >
              <Page pageNumber={pageNumber} scale={scale} renderTextLayer={false} renderAnnotationLayer={false} />
            </Document>
          )}

          {!pdfFile && !error && !loadingDoc &&(
            <Stack alignItems="center" justifyContent="center" height="100%">
              <Typography>No document to preview</Typography>
            </Stack>
          )}
        </PDFContent>
      </ModalDialog>
    </Modal>
  );
};

export { PreviewDocument };
